import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { resumeAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { FileText, Plus, MoreVertical, Edit, Trash2, Eye, Loader2, Upload, Sparkles, CheckCircle2 } from 'lucide-react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const Resumes = () => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadResult, setShowUploadResult] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResumes = async () => {
      try {
        const response = await resumeAPI.getAll();
        setResumes(response.data);
      } catch (error) {
        toast.error('Failed to load resumes');
      } finally {
        setLoading(false);
      }
    };
    fetchResumes();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) return;
    
    try {
      await resumeAPI.delete(id);
      setResumes(resumes.filter(r => r.id !== id));
      toast.success('Resume deleted');
    } catch (error) {
      toast.error('Failed to delete resume');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    const validExtensions = ['.pdf', '.docx', '.doc', '.txt'];
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!validTypes.includes(file.type) && !hasValidExtension) {
      toast.error('Please upload a PDF, DOCX, or TXT file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    try {
      const response = await resumeAPI.upload(file);
      setUploadResult(response.data);
      setShowUploadResult(true);
      toast.success('Resume analyzed successfully!');
    } catch (error) {
      console.debug('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload resume');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCreateFromUpload = async () => {
    if (!uploadResult?.parsed_resume) return;

    try {
      const resumeData = {
        title: `Imported Resume - ${new Date().toLocaleDateString()}`,
        personal_info: uploadResult.parsed_resume.personal_info || {},
        summary: uploadResult.parsed_resume.summary || '',
        experience: uploadResult.parsed_resume.experience || [],
        education: uploadResult.parsed_resume.education || [],
        skills: uploadResult.parsed_resume.skills || [],
      };

      const response = await resumeAPI.create(resumeData);
      toast.success('Resume created! Redirecting to editor...');
      setShowUploadResult(false);
      navigate(`/resumes/${response.data.id}/edit`);
    } catch (error) {
      toast.error('Failed to create resume');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="resumes-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              My Resumes
            </h1>
            <p className="text-slate-600 mt-1">
              Create and manage your professional resumes
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Upload Existing Resume */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".pdf,.docx,.doc,.txt"
              className="hidden"
              id="resume-upload"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-resume-btn"
              className="text-xs sm:text-sm"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 sm:mr-2" />
              )}
              <span className="hidden sm:inline">{uploading ? 'Analyzing...' : 'Upload'}</span>
            </Button>
            <Link to="/resumes/new">
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm" data-testid="create-resume-btn">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">New Resume</span>
                <span className="sm:hidden">New</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Upload Result Dialog */}
        <Dialog open={showUploadResult} onOpenChange={setShowUploadResult}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-600" />
                Resume Analysis Complete
              </DialogTitle>
              <DialogDescription>
                We've analyzed your resume and extracted the information. Review the details below and create an editable version.
              </DialogDescription>
            </DialogHeader>
            
            {uploadResult?.parsed_resume && (
              <div className="space-y-4 py-4">
                {/* Personal Info */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Personal Information</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p><span className="text-slate-500">Name:</span> {uploadResult.parsed_resume.personal_info?.name || 'Not found'}</p>
                    <p><span className="text-slate-500">Email:</span> {uploadResult.parsed_resume.personal_info?.email || 'Not found'}</p>
                    <p><span className="text-slate-500">Phone:</span> {uploadResult.parsed_resume.personal_info?.phone || 'Not found'}</p>
                    <p><span className="text-slate-500">Location:</span> {uploadResult.parsed_resume.personal_info?.location || 'Not found'}</p>
                  </div>
                </div>

                {/* Experience Count */}
                <div className="flex items-center gap-4">
                  <div className="bg-indigo-50 rounded-lg px-4 py-2">
                    <span className="text-indigo-600 font-semibold">{uploadResult.parsed_resume.experience?.length || 0}</span>
                    <span className="text-slate-600 text-sm ml-1">Jobs Found</span>
                  </div>
                  <div className="bg-emerald-50 rounded-lg px-4 py-2">
                    <span className="text-emerald-600 font-semibold">{uploadResult.parsed_resume.education?.length || 0}</span>
                    <span className="text-slate-600 text-sm ml-1">Education</span>
                  </div>
                  <div className="bg-purple-50 rounded-lg px-4 py-2">
                    <span className="text-purple-600 font-semibold">{uploadResult.parsed_resume.skills?.length || 0}</span>
                    <span className="text-slate-600 text-sm ml-1">Skills</span>
                  </div>
                </div>

                {/* Improvement Suggestions */}
                {uploadResult.parsed_resume.improvement_suggestions?.length > 0 && (
                  <div className="bg-amber-50 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      AI Improvement Suggestions
                    </h4>
                    <ul className="space-y-2">
                      {uploadResult.parsed_resume.improvement_suggestions.map((suggestion, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-amber-900">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadResult(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateFromUpload} className="bg-indigo-600 hover:bg-indigo-700">
                <Edit className="w-4 h-4 mr-2" />
                Create & Edit Resume
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Resumes Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : resumes.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resumes.map((resume) => (
              <Card key={resume.id} className="card-hover" data-testid={`resume-card-${resume.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-indigo-600" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`resume-menu-${resume.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/resumes/${resume.id}`)} className="cursor-pointer">
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/resumes/${resume.id}/edit`)} className="cursor-pointer">
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(resume.id)} className="text-red-600 cursor-pointer">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-1">
                    {resume.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    {resume.personal_info?.name || 'No name set'}
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Updated {formatDate(resume.updated_at)}</span>
                    <span>{resume.skills?.length || 0} skills</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No resumes yet</h2>
            <p className="text-slate-600 mb-6">Create a new AI-powered resume or upload an existing one to improve it</p>
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Upload Existing Resume
              </Button>
              <Link to="/resumes/new">
                <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-first-resume-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create New Resume
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Resumes;
